import Link from "components/elements/link"
import PropTypes from "prop-types"
import React from "react"
import { getOptimizedImage } from "utils/optimizeContentfulImage"
import {
  FaxIcon,
  PhoneIcon,
  PinIcon,
} from "components/blocks/clinicInfo/infoIcons"
import "./locationListCard.scss"

// One center in the all-locations list. Photo, city and state, then the details
// a parent needs before deciding to call: address, phone, fax.
//
// distance is only set once we know where the visitor is, either because they
// allowed location access or because they searched. It stays hidden otherwise
// rather than showing a distance from nowhere.

const ArrowIcon = () => (
  <svg
    aria-hidden="true"
    className="LocationListCard__arrow"
    fill="none"
    viewBox="0 0 24 24"
  >
    <path
      d="M5 12h13m0 0-5-5m5 5-5 5"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    />
  </svg>
)

const LocationListCard = ({
  detailedAddress,
  distance,
  fax,
  image,
  innerRef,
  isHighlighted,
  name,
  phone,
  slug,
}) => (
  <div
    className={`LocationListCard${
      isHighlighted ? " LocationListCard--highlighted" : ""
    }`}
    ref={innerRef}
  >
    {image && image.file ? (
      <div className="LocationListCard__media">
        <img
          alt={image.alt || `Cortica ${name}`}
          loading="lazy"
          src={getOptimizedImage(image.file.url, "clinicCard")}
        />
      </div>
    ) : (
      <div className="LocationListCard__media LocationListCard__media--empty" />
    )}

    <div className="LocationListCard__body">
      <div className="LocationListCard__heading">
        <Link className="LocationListCard__name" to={slug}>
          {name}
        </Link>
        {typeof distance === "number" && (
          <p className="LocationListCard__distance">
            {distance < 1 ? "Less than a mile" : `${distance} miles`} away
          </p>
        )}
      </div>

      <ul className="LocationListCard__details">
        {detailedAddress && (
          <li className="LocationListCard__detail">
            <PinIcon />
            <span>{detailedAddress}</span>
          </li>
        )}
        {phone && (
          <li className="LocationListCard__detail">
            <PhoneIcon />
            <span>
              <span className="LocationListCard__label">Phone:</span>{" "}
              <a href={`tel:${phone}`}>{phone}</a>
            </span>
          </li>
        )}
        {fax && (
          <li className="LocationListCard__detail">
            <FaxIcon />
            <span>
              <span className="LocationListCard__label">Fax:</span> {fax}
            </span>
          </li>
        )}
      </ul>

      <Link className="LocationListCard__cta" to={slug}>
        View Location
        <ArrowIcon />
      </Link>
    </div>
  </div>
)

export const LocationListCardProps = {
  detailedAddress: PropTypes.string,
  distance: PropTypes.number,
  fax: PropTypes.string,
  image: PropTypes.shape({
    alt: PropTypes.string,
    file: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  name: PropTypes.string,
  phone: PropTypes.string,
  slug: PropTypes.string,
}

LocationListCard.propTypes = {
  ...LocationListCardProps,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isHighlighted: PropTypes.bool,
}

export default LocationListCard
